/**
 * 📨 Postacı — Bildirim Servisi
 * Tüm bildirim CRUD işlemleri burada merkezileştirilir.
 * UI bileşenlerinde doğrudan supabase.from('notifications') kullanılmaz.
 */
import { supabase } from '@/lib/supabase'
import type { Notification } from '@/types/database.types'
import { logError } from '@/lib/log'
import { withTransientRetry } from '@/lib/retryTransient'

const NOTIFICATION_FETCH_LIMIT = 50
const CLOCK_SKEW_RETRY_DELAY_MS = 1000

/** Son 50 bildirimi getir */
export async function fetchNotifications(userId: string): Promise<Notification[]> {
  // Okuma: geçici hatada (ör. PGRST303 "JWT issued at future") tekrar güvenli.
  // Sunucular arası saat farkı 400 ms'yi aştı (GUNUM-VAR-1Z); bekleme bu yüzden 1 sn.
  const { data, error } = await withTransientRetry(
    async () =>
      await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(NOTIFICATION_FETCH_LIMIT),
    { maxRetries: 1, delayMs: CLOCK_SKEW_RETRY_DELAY_MS }
  )

  if (error) {
    throw new Error(`Bildirimler yüklenemedi: ${error.message}`)
  }

  return (data ?? []) as unknown as Notification[]
}

/** Tekil bildirimi okundu işaretle */
export async function markAsRead(notificationId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('id', notificationId)

  if (error) {
    throw new Error(`Bildirim güncellenemedi: ${error.message}`)
  }
}

/** Tekil bildirimi okunmadı işaretle */
export async function markAsUnread(notificationId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: false })
    .eq('id', notificationId)

  if (error) {
    throw new Error(`Bildirim okundu durumu kaldırılamadı: ${error.message}`)
  }
}

/** Tüm bildirimleri okundu işaretle (RPC) */
export async function markAllAsRead(): Promise<void> {
  const { error } = await supabase.rpc('mark_all_read')

  if (error) {
    throw new Error(`Toplu okundu işareti başarısız: ${error.message}`)
  }
}

/** Bildirim sil */
export async function deleteNotification(notificationId: string): Promise<void> {
  const { error } = await supabase.from('notifications').delete().eq('id', notificationId)

  if (error) {
    throw new Error(`Bildirim silinemedi: ${error.message}`)
  }
}

/** Okunmuş tüm bildirimleri sil */
export async function deleteReadNotifications(userId: string): Promise<void> {
  const { error } = await supabase.from('notifications').delete().eq('user_id', userId).eq('is_read', true)

  if (error) {
    throw new Error(`Okunan bildirimler silinemedi: ${error.message}`)
  }
}

/** Okunmamış bildirim sayısını getir */
export async function getUnreadCount(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('is_read', false)

  if (error) {
    throw new Error(`Okunmamış sayısı alınamadı: ${error.message}`)
  }

  return count ?? 0
}

/** Yeni bildirim oluştur */
export async function createNotification(
  userId: string,
  type: string,
  data: any = {},
  groupKey?: string
): Promise<void> {
  const { Postaci } = await import('@/services/postaci')
  
  const response = await Postaci.send({
    userId,
    templateSlug: type,
    variables: data,
    groupKey
  })

  if (!response.success) {
    logError('[createNotification] ❌ Postaci Error:', response.error)
    throw new Error(`Bildirim oluşturulamadı: ${response.error}`)
  }
}


/**
 * Bir işleme bağlı `payment_declared` bildirimini okundu işaretler.
 *
 * İşlem kimliği boşsa SORGU YAPILMAZ: filtre boş değerle çalışırsa yanlış
 * bildirimler okundu işaretlenebilir.
 */
export async function markPaymentNotificationsRead(
  transactionId: string
): Promise<{ error: string | null }> {
  if (!transactionId) return { error: 'İşlem kimliği boş' }

  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('type', 'payment_declared')
    .filter('data->>transactionId', 'eq', transactionId)

  if (error) {
    logError('[notificationService] bildirim okundu yapılamadı:', error)
    return { error: error.message }
  }
  return { error: null }
}

export interface DirectNotificationInput {
  userId: string
  title: string
  body: string
  type: string
  data?: Record<string, unknown>
}

/**
 * Doğrudan bildirim satırı yazar.
 *
 * `createNotification`'dan FARKLI: o Postacı şablon motorundan geçer, bu
 * ise başlığı ve gövdeyi çağıran taraftan alır. Şablon dışı tek kullanım
 * davet hatırlatması; ileride şablona çevrilebilir.
 */
export async function insertNotification(
  input: DirectNotificationInput
): Promise<{ error: string | null }> {
  if (!input.userId) return { error: 'Kullanıcı kimliği boş' }
  if (!input.type) return { error: 'Bildirim türü boş' }

  const { error } = await supabase.from('notifications').insert({
    user_id: input.userId,
    title: input.title,
    body: input.body,
    type: input.type,
    data: (input.data ?? {}) as never,
  })

  if (error) {
    logError('[notificationService] bildirim yazılamadı:', error)
    return { error: error.message }
  }
  return { error: null }
}


/**
 * Bildirimin tıklandığı anı iletişim günlüğüne yazar.
 *
 * Kimlik boşsa SORGU YAPILMAZ: `contains` boş değerle çalışırsa yanlış
 * kayıtlar işaretlenebilir.
 */
export async function markNotificationClicked(
  notificationId: string
): Promise<{ error: string | null }> {
  if (!notificationId) return { error: 'Bildirim kimliği boş' }

  const { error } = await supabase
    .from('communication_logs')
    .update({ clicked_at: new Date().toISOString() } as never)
    .contains('metadata', { notificationId })

  if (error) {
    logError('[notificationService] tıklama kaydı yazılamadı:', error)
    return { error: error.message }
  }
  return { error: null }
}

/** Kullanıcının kayıtlı cihaz sayısı. Okunamazsa 0. */
export async function countUserDevices(userId: string): Promise<number> {
  if (!userId) return 0

  const { count, error } = await supabase
    .from('user_devices')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)

  if (error) {
    logError('[notificationService] cihaz sayısı okunamadı:', error)
    return 0
  }
  return count ?? 0
}
